import { groupBy } from "@/utils/misc";
import Text from "@ingka/text";
import { AssetDto, UploadWithSlimAssetsDtoStateEnum } from "generated";
import AssetBlock from "./AssetBlock";
import { titleLookup } from "./constants";
import { Fragment, useState } from "react";
import GenericModal from "../GenericModal/GenericModal";
import { AuthenticatedImage } from "../AuthenticatedResource/AuthenticatedImage";
import Carousel from "@ingka/carousel";
import { getAriaLabel } from "./utils";
import { ModalState } from "./types";
import { AuthenticatedVideo } from "../AuthenticatedResource/AuthenticatedVideo";
import { AssetValidationEntry } from "@/services/store/slices/mandatoryMetaData/types";

type Props = {
  assets: AssetDto[];
  isChecked: string[];
  visibility: string[];
  errors: AssetValidationEntry[];
  loadingSimilarAssets: boolean;
  uploadState: UploadWithSlimAssetsDtoStateEnum;
  handleChangeChecked: (id: string) => void;
  onSimilarAssets: (assetId: number) => void;
};

export const AdminAssetListView = ({
  assets,
  isChecked,
  handleChangeChecked,
  visibility,
  errors,
  loadingSimilarAssets,
  onSimilarAssets,
  uploadState,
}: Props) => {
  const [carouselSlideIndex, setCarouselSlideIndex] = useState<number | null>(
    null,
  );

  const groupedAssets = groupBy(assets, "state");
  const displayedAssets = Object.entries(titleLookup).flatMap(
    ([key]) => groupedAssets[key] || [],
  );

  return Object.entries(titleLookup).map(([key, title]) => {
    const assets = groupedAssets[key];
    return (
      assets && (
        <Fragment key={key}>
          {title && (
            <Text className="ml-[40px] mt-[40px]" headingSize="xs">
              {title}
            </Text>
          )}
          {assets.map((asset) => {
            const assetId = asset.id;
            return (
              <AssetBlock
                key={assetId}
                asset={asset}
                isChecked={isChecked.includes(assetId.toString())}
                visibility={visibility}
                loadingSimilarAssets={loadingSimilarAssets}
                errors={errors?.filter((e) => e.assetId === assetId)}
                onChecked={() => handleChangeChecked(assetId.toString())}
                onSimilarAssets={onSimilarAssets}
                uploadState={uploadState}
                onOpenImageGallery={() => {
                  const index = displayedAssets.findIndex(
                    (a) => a.id === assetId,
                  );
                  setCarouselSlideIndex(index);
                }}
              />
            );
          })}
          <GenericModal
            modalVisible={carouselSlideIndex !== null}
            hideModal={() => setCarouselSlideIndex(null)}
            ariaLabel={getAriaLabel(ModalState.Image)}
            modalType="theatre"
            className="left-[10%] w-[80%]"
          >
            <div className="flex h-screen w-full items-center justify-center">
              <Carousel
                id="asset-carousel"
                skipButtonText="Skip listing"
                ariaLabelLeftBtn="Previous asset"
                ariaLabelRightBtn="Next asset"
                slideIndex={carouselSlideIndex ?? 0}
                isSlideShow
              >
                {displayedAssets.map(({ id, contentType }) => {
                  return (
                    <Fragment key={id}>
                      {contentType === "IMAGE" && (
                        <AuthenticatedImage
                          thumbnail={false}
                          assetId={id}
                          className="object-contain"
                        />
                      )}
                      {contentType === "VIDEO" && (
                        <AuthenticatedVideo thumbnail={false} assetId={id} />
                      )}
                    </Fragment>
                  );
                })}
              </Carousel>
            </div>
          </GenericModal>
        </Fragment>
      )
    );
  });
};
